import * as THREE from 'three'
import { XRButton } from 'three/addons/webxr/XRButton.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import gsap from 'gsap';

// Configure GSAP for optimal performance with Three.js
gsap.ticker.lagSmoothing(0);
gsap.ticker.fps(60);

// Reusable vectors and quaternion to avoid per-frame allocations
const _vA = new THREE.Vector3();
const _vB = new THREE.Vector3();
const _vC = new THREE.Vector3();
const _q = new THREE.Quaternion();
const UP_VECTOR = new THREE.Vector3(0, 1, 0);

// Fresnel shader globals
let fresnelVertexShader = '';
let fresnelFragmentShader = '';
let fresnelMaterial = null;

// Sizes shader globals
let sizesVertexShader = '';
let sizesFragmentShader = '';
let sizesMaterial = null;

function preloadFresnelShaders() {
	// Load once
	if (fresnelVertexShader && fresnelFragmentShader) return Promise.resolve();
	const vPromise = fetch('/static/shaders/fresnel_vertex.glsl').then(r => r.text()).then(src => { fresnelVertexShader = src; });
	const fPromise = fetch('/static/shaders/fresnel_fragment.glsl').then(r => r.text()).then(src => { fresnelFragmentShader = src; });
	return Promise.all([vPromise, fPromise]).then(() => undefined).catch(err => console.error('Failed to load Fresnel shaders:', err));
}

function preloadSizesShaders() {
	// Load once
	if (sizesVertexShader && sizesFragmentShader) return Promise.resolve();
	const vPromise = fetch('/static/shaders/sizes_vertex.glsl').then(r => r.text()).then(src => { sizesVertexShader = src; });
	const fPromise = fetch('/static/shaders/sizes_fragment.glsl').then(r => r.text()).then(src => { sizesFragmentShader = src; });
	return Promise.all([vPromise, fPromise]).then(() => undefined).catch(err => console.error('Failed to load Sizes shaders:', err));
}

function getOrCreateFresnelMaterial() {
	if (!fresnelMaterial) {
		fresnelMaterial = new THREE.ShaderMaterial({
			vertexShader: fresnelVertexShader,
			fragmentShader: fresnelFragmentShader,
			uniforms: {
				outlineThickness: { value: 2.0 },
				outlineColor: { value: new THREE.Color(0xFFFF00) }
			},
			side: THREE.FrontSide,
			transparent: true,
			depthTest: true,
			depthWrite: true
		});
	}
	return fresnelMaterial;
}

function getOrCreateSizesMaterial() {
	if (!sizesMaterial) {
		sizesMaterial = new THREE.ShaderMaterial({
			vertexShader: sizesVertexShader,
			fragmentShader: sizesFragmentShader,
			uniforms: {
				baseColor: { value: new THREE.Color(0xffffff) },
				opacity: { value: 0.8 }
			},
			side: THREE.DoubleSide,
			transparent: true,
			depthTest: false,
			depthWrite: false
		});
	}
	return sizesMaterial;
}

function storeOriginalMaterialsIfMissing(mesh) {
	if (!mesh || !mesh.isMesh) return;
	if (!mesh.userData) mesh.userData = {};
	if (mesh.userData.originalMaterialStored) return;
	mesh.userData.originalMaterial = Array.isArray(mesh.material) ? mesh.material.slice() : mesh.material;
	mesh.userData.originalMaterialStored = true;
}

function applyFresnelToChair() {
	if (!chairModel) return;
	// Ensure shaders are ready
	if (!fresnelVertexShader || !fresnelFragmentShader) {
		// Load and retry after load completes
		preloadFresnelShaders().then(() => applyFresnelToChair());
		return;
	}
	const sharedFresnel = getOrCreateFresnelMaterial();
	chairModel.traverse((child) => {
		// Only apply fresnel to original chair meshes, not sizes.glb meshes
		if (child.isMesh && child.material && child.userData.isOriginalChair) {
			storeOriginalMaterialsIfMissing(child);
			if (Array.isArray(child.material)) {
				for (let i = 0; i < child.material.length; i++) child.material[i] = sharedFresnel;
			} else {
				child.material = sharedFresnel;
			}
			child.material.needsUpdate = true;
		}
	});
}

function restoreOriginalMaterials() {
	if (!chairModel) return;
	chairModel.traverse((child) => {
		// Only restore original materials for original chair meshes, not sizes.glb meshes
		if (child.isMesh && child.userData && child.userData.originalMaterialStored && child.userData.isOriginalChair) {
			child.material = child.userData.originalMaterial;
			child.material && (Array.isArray(child.material) ? child.material.forEach(m => m && (m.needsUpdate = true)) : (child.material.needsUpdate = true));
		}
	});
}

let renderer;
let lPinchOn;
let rPinchOn;
let baseComposition;
let chairModel;
let switchButton;
let switchDot;
let switchRoot;
let switchButtonAttached = false;
let switchButtonOriginalPosition = new THREE.Vector3();
let switchPullCount = 0;
let stringMesh;
let buttonState = false; // false = off, true = on
let headerToggleSwitchMesh; // Toggle switch plane mesh
let togglePosA; // Left slot local position
let togglePosB; // Right slot local position
let headerToggleIsRight = false; // false = left (Material), true = right (Sizes)
let delta;
let uiFrame; 
let infoPanel; 
let headerToggle;
let pullPlane; 
let colorPanel; 
let button1Mesh; // Button 1 (Blue)
let button2Mesh; // Button 2 (Red) 
let button3Mesh; // Button 3 (Orange)
let buttonStroke1Mesh; // Button 1 stroke
let buttonStroke2Mesh; // Button 2 stroke  
let buttonStroke3Mesh; // Button 3 stroke
let materialTextMesh; // Material text mesh
let sizesTextMesh; // Sizes text mesh
let sizeTextMesh0; // Size text mesh 0
let sizeTextMesh1; // Size text mesh 1
let sizeTextMesh2; // Size text mesh 2
let sizeTextMesh3; // Size text mesh 3
let sizesModel; // Sizes model reference for visibility control


// Cooldown for header toggle touch flipping
let headerToggleLastTouchMs = 0;
const HEADER_TOGGLE_TOUCH_COOLDOWN_MS = 400;


// Add global variables for Plastic material references
let plasticMaterials = []; // Array to store all Plastic materials for easy access

// Button cooldown system to prevent rapid repeated triggers
let buttonCooldowns = {
    button1: 0, // Blue button
    button2: 0, // Red button  
    button3: 0  // Orange button
};
const BUTTON_COOLDOWN_TIME = 500; // 500ms cooldown between button presses

// Button reset delay system to prevent reset animations from interfering with press animations
let buttonResetTimers = {
    button1: 0,
    button2: 0,
    button3: 0
};
const BUTTON_RESET_DELAY = 300; // 300ms delay before reset

// Chair rotation variables
let leftHandRotationActive = false;
let rightHandRotationActive = false;
let lastRightHandX = 0;
let lastLeftHandX = 0;
let chairRotationSpeed = 5.0;
// Scene
const scene = new THREE.Scene()
scene.background = new THREE.Color( 0x505050 )

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Camera
const camera = new THREE.PerspectiveCamera( 50, window.innerWidth / window.innerHeight, 0.1, 50 );
camera.position.set( 0, 1.6, 3 );
scene.add(camera)

// Renderer - Optimized for WebXR performance
renderer = new THREE.WebGLRenderer({
    antialias: false,              // XR AA is expensive; keep quality via resolution
    stencil: false,
    depth: true,
    powerPreference: 'high-performance',
    alpha: false                   // no transparent canvas; lets GPU do fast clears
});
// Dynamic resolution scaling setup
const MAX_PR = 1.5; // Balanced value for quality vs performance
let adaptivePR = Math.min(window.devicePixelRatio, MAX_PR);
const targetMs = 1000 / 72; // ~72Hz baseline
let accum = 0;
renderer.setPixelRatio(adaptivePR);
renderer.setSize( window.innerWidth, window.innerHeight );
renderer.xr.enabled = true;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap; // More performant for XR
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
document.body.appendChild( renderer.domElement );

// Create XR button but do NOT append it; keep it detached and hidden
const xrButton = XRButton.createButton( renderer, { 'optionalFeatures': [ 'hand-tracking' ] } );
xrButton.style.display = 'none';

// Proactively remove any XR/VR/AR buttons injected by libs or browser UI
['VRButton','ARButton','XRButton'].forEach(id => {
    const el = document.getElementById(id);
    if (el && el.parentNode) el.parentNode.removeChild(el);
});
document.querySelectorAll('.vr-button, .ar-button, .webxr-button, .xr-button, button[aria-label="Enter VR"], button[aria-label="Enter XR"], button[aria-label="Enter AR"]').forEach(el => {
    if (el && el.parentNode) el.parentNode.removeChild(el);
});

// Make the entire page clickable for entering WebXR
document.body.style.cursor = 'pointer';
document.body.addEventListener('click', () => {
    // Simply click the hidden XR button to trigger WebXR session
    // This uses the same logic as the original XRButton
    xrButton.click();
});

// Add visual feedback for the clickable area
document.body.style.transition = 'background-color 0.3s ease';
document.body.addEventListener('mouseenter', () => {
    document.body.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
});
document.body.addEventListener('mouseleave', () => {
    document.body.style.backgroundColor = '';
});

// Function to create procedural textures for environment map
function createProceduralTexture(width, height, color) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // Create a simple gradient from the color to a lighter version
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, `#${color.toString(16).padStart(6, '0')}`);
    gradient.addColorStop(1, `#${(color + 0x222222).toString(16).padStart(6, '0')}`);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    return canvas;
}

// Create environment map for realistic reflections
const pmremGenerator = new THREE.PMREMGenerator(renderer);
pmremGenerator.compileEquirectangularShader();

// Load and process HDR environment map using PMREM for better performance
const rgbeLoader = new RGBELoader();
rgbeLoader.load('/static/2k.hdr', (hdr) => {
    const pmrem = pmremGenerator.fromEquirectangular(hdr);
    scene.environment = pmrem.texture;  // prefiltered, mip-mapped for efficient reflections
    hdr.dispose();                      // free up original HDR texture
    pmremGenerator.dispose();           // free up generator after compilation
    // scene.background = null;         // keep background flat color to avoid heavy sky draws
});

// Base Composition (container for the chair)
const tempGeo = new THREE.BoxGeometry(0.3, 0.3, 0.3);
const tempMat = new THREE.MeshBasicMaterial({ 
    color: 0xffffff, 
    transparent: true,
    opacity: 0.0,
    depthTest: false,
    depthWrite: false
});
baseComposition = new THREE.Mesh(tempGeo, tempMat);
baseComposition.scale.set(1, 1, 1); 
scene.add(baseComposition);
// Hide all 3D content in 2D/desktop view; show only when WebXR starts
baseComposition.visible = false;

// Auto-position composition at user's head height
let compositionPositioned = false;
let xrSessionStartTime = null;

function positionCompositionAtHeadHeight() {
    if (compositionPositioned || !xrSessionStartTime) return;
    
    // Wait 1 seconds after WebXR session starts
    const elapsedTime = Date.now() - xrSessionStartTime;
    if (elapsedTime < 1000) {
        return;
    }
    
    // Get user's head position (camera position)
    const headHeight = camera.position.y;
    const headDistance = camera.position.z;
    
    // Position composition at head height, slightly in front of user
    baseComposition.position.set(0, headHeight - 0.2, headDistance - 1.0);
    
    compositionPositioned = true;

}

renderer.xr.addEventListener('sessionstart', () => {
    xrSessionStartTime = Date.now();
    compositionPositioned = false;
    // Add class to hide the instruction overlay
    document.body.classList.add('xr-presenting');
    // Reveal 3D content only in XR
    baseComposition.visible = true;
});

renderer.xr.addEventListener('sessionend', () => {
    xrSessionStartTime = null;
    compositionPositioned = false; // Reset for next session
    // Remove class to show the instruction overlay again
    document.body.classList.remove('xr-presenting');
    // Hide 3D content again in 2D
    baseComposition.visible = false;
});

// Load the chair GLB file
const loader = new GLTFLoader();
loader.load(
    '/static/chair.glb', 
    function (gltf) {
        chairModel = gltf.scene;
        
        chairModel.scale.set(0.5, 0.5, 0.5);        
        chairModel.position.set(0, -0.25, 0);        
        chairModel.rotation.set(0, -0.7, 0); 

        // Enable shadows for the chair and mark as original chair mesh
        chairModel.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                // Mark as original chair mesh to distinguish from sizes.glb meshes
                child.userData.isOriginalChair = true;
            }
        });

        baseComposition.add(chairModel);
        
        // Improve materials with PBR properties for better realism
        improveMaterials(chairModel);
        
        // Store reference to improveMaterials function for later use
        window.refreshChairMaterials = () => improveMaterials(chairModel);
        
        // Load the sizes.glb file after chair is loaded
        loader.load(
            '/static/sizes.glb',
            function (gltf) {
                sizesModel = gltf.scene;
                
                // Don't set transformations - it will inherit from chair as parent
                
                // Preload shaders and create custom material
                preloadSizesShaders().then(() => {
                    const customSizesMaterial = getOrCreateSizesMaterial();
                    
                    // Apply custom shader material to all meshes
                    sizesModel.traverse((child) => {
                        if (child.isMesh) {
                            child.material = customSizesMaterial;
                            child.castShadow = true;
                            child.receiveShadow = true;
                            child.renderOrder = 1000; // Render on top of fresnel
                        }
                    });
                    
                }).catch(err => {
                    // Use fallback material if shaders fail to load
                    
                    // Fallback to basic material if shaders fail to load
                    const unlitWhiteMaterial = new THREE.MeshBasicMaterial({
                        color: 0xffffff,
                        transparent: false,
                        side: THREE.DoubleSide,
                        depthTest: false,
                        depthWrite: false
                    });
                    
                    sizesModel.traverse((child) => {
                        if (child.isMesh) {
                            child.material = unlitWhiteMaterial;
                            child.castShadow = true;
                            child.receiveShadow = true;
                            child.renderOrder = 1000; // Render on top of fresnel
                        }
                    });
                });
                
                // Add sizes model as child to chair (will inherit chair's transformations)
                chairModel.add(sizesModel);
                
                // Make sizes model invisible by default
                sizesModel.visible = false;
                
                // Add text object with value "16.0''" as child to sizes model
                const fontLoader = new FontLoader();
                fontLoader.load('/static/fonts/helvetiker_regular.typeface.json', function (font) {
                    // Create text geometry for the size value
                    const sizeTextGeometry = new TextGeometry('16.0"', {
                        font: font,
                        size: 0.04,
                        height: 0,
                        curveSegments: 12,
                        bevelEnabled: false
                    });
                    
                    // Create white material for the text
                    const sizeTextMaterial = new THREE.MeshBasicMaterial({ 
                        color: 0xffffff,
                        transparent: true,
                        opacity: 1.0
                    });
                    
                    // Create the text mesh
                    sizeTextMesh0 = new THREE.Mesh(sizeTextGeometry, sizeTextMaterial);
                    
                    // Center the text
                    sizeTextGeometry.computeBoundingBox();
                    sizeTextMesh0.position.x -= sizeTextGeometry.boundingBox.max.x * 0.5;
                    sizeTextMesh0.position.y = 0.2; // Position above the sizes model
                    sizeTextMesh0.position.z = 0.03; // Slight forward offset
                    
                    // Add as child to sizes model
                    sizesModel.add(sizeTextMesh0);
                    
                    // Create second text geometry for another size value
                    const sizeTextGeometry1 = new TextGeometry('31.5"', {
                        font: font,
                        size: 0.04,
                        height: 0,
                        curveSegments: 12,
                        bevelEnabled: false
                    });
                    
                    // Create white material for the second text
                    const sizeTextMaterial1 = new THREE.MeshBasicMaterial({ 
                        color: 0xffffff,
                        transparent: true,
                        opacity: 1.0
                    });
                    
                    // Create the second text mesh
                    sizeTextMesh1 = new THREE.Mesh(sizeTextGeometry1, sizeTextMaterial1);
                    
                    // Center the second text
                    sizeTextGeometry1.computeBoundingBox();
                    sizeTextMesh1.position.x = -sizeTextGeometry1.boundingBox.max.x * 0.5 + 0.06;
                    sizeTextMesh1.position.y = 0.43; // Position below the first text
                    sizeTextMesh1.position.z = -0.34; // Backward offset
                    sizeTextMesh1.rotation.y = Math.PI / 2; // Rotate 90 degrees around Y-axis
                    
                    // Add as child to sizes model
                    sizesModel.add(sizeTextMesh1);
                    
                    // Create third text geometry for another size value
                    const sizeTextGeometry2 = new TextGeometry('24.5"', {
                        font: font,
                        size: 0.04,
                        height: 0,
                        curveSegments: 12,
                        bevelEnabled: false
                    });
                    
                    // Create white material for the third text
                    const sizeTextMaterial2 = new THREE.MeshBasicMaterial({ 
                        color: 0xffffff,
                        transparent: true,
                        opacity: 1.0
                    });
                    
                    // Create the third text mesh
                    sizeTextMesh2 = new THREE.Mesh(sizeTextGeometry2, sizeTextMaterial2);
                    
                    // Center the third text
                    sizeTextGeometry2.computeBoundingBox();
                    sizeTextMesh2.position.x = -sizeTextGeometry2.boundingBox.max.x * 0.5;
                    sizeTextMesh2.position.y = 0; // Position at Y=0
                    sizeTextMesh2.position.z = 0.27; // Forward offset
                    sizeTextMesh2.rotation.x = -Math.PI / 2; // Rotate -90 degrees around X-axis
                    
                    // Add as child to sizes model
                    sizesModel.add(sizeTextMesh2);
                    
                    // Create fourth text geometry for another size value
                    const sizeTextGeometry3 = new TextGeometry('23.5"', {
                        font: font,
                        size: 0.04,
                        height: 0,
                        curveSegments: 12,
                        bevelEnabled: false
                    });
                    
                    // Create white material for the fourth text
                    const sizeTextMaterial3 = new THREE.MeshBasicMaterial({ 
                        color: 0xffffff,
                        transparent: true,
                        opacity: 1.0
                    });
                    
                    // Create the fourth text mesh
                    sizeTextMesh3 = new THREE.Mesh(sizeTextGeometry3, sizeTextMaterial3);
                    
                    // Center the fourth text with individual position values
                    sizeTextGeometry3.computeBoundingBox();
                    sizeTextMesh3.position.x = -sizeTextGeometry3.boundingBox.max.x * 0.5 + 0.35;
                    sizeTextMesh3.position.y = 0; // Position at Y=0
                    sizeTextMesh3.position.z = 0.06; // Z position at 0.06
                    sizeTextMesh3.rotation.x = sizeTextMesh2.rotation.x; // Same X rotation
                    sizeTextMesh3.rotation.y = 0; // No Y rotation
                    sizeTextMesh3.rotation.z = Math.PI / 2; // Rotate 90 degrees around Z-axis
                    
                    // Add as child to sizes model
                    sizesModel.add(sizeTextMesh3);
                    
                });
                // Sizes model loaded and attached as child to chair (invisible by default)
            },
            function (xhr) {
                // Loading progress
            },
            function (error) {
                // Handle error loading sizes model
            }
        );
        
        // UI Frame
        const frameGeometry = new THREE.PlaneGeometry(0.4, 0.6); // Slightly smaller than the cube 
        const textureLoader = new THREE.TextureLoader();
        const uiFrameTexture = textureLoader.load('/static/ui_frame.png'); 
        const frameMaterial = new THREE.MeshBasicMaterial({ 
            map: uiFrameTexture,
            transparent: true,
            opacity: 0.4,
            side: THREE.FrontSide, // Optimized: removed DoubleSide
            //depthTest: false,
            //depthWrite: false,
        });
        const frame = new THREE.Mesh(frameGeometry, frameMaterial);
        uiFrame = frame; // Assign to top-level variable
        frame.position.set(0, 0, 0);
        baseComposition.add(frame);
        
        // Header Toggle
        const headerToggleGeometry = new THREE.PlaneGeometry(0.38666, 0.0661);
        const uiHeaderTexture = textureLoader.load('/static/ui_header-toggle.png');
        const headerToggleMaterial = new THREE.MeshBasicMaterial({ 
            map: uiHeaderTexture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });
        const headerToggleMesh = new THREE.Mesh(headerToggleGeometry, headerToggleMaterial);
        headerToggle = headerToggleMesh; // Assign to top-level variable
        //headerToggleMesh.position.set(-0.05, 0.26, 0);
        headerToggleMesh.position.set(0, 0.26, 0);
        baseComposition.add(headerToggleMesh);
        headerToggleMesh.scale.set(0, 0, 0);

        // Header Toggle Switch (child plane with ui_header-toggle-switch.png)
        // Calculate size based on 145x44 proportion at 43.2% of parent size
        // Using 43.2% of parent width and calculating height proportionally
        const switchWidth = 0.38666 * 0.432; // 43.2% of parent width
        const switchHeight = (switchWidth * 44) / 145; // Calculate height based on 145:44 ratio
        const headerToggleSwitchGeometry = new THREE.PlaneGeometry(switchWidth, switchHeight);
        const uiHeaderSwitchTexture = textureLoader.load('/static/ui_header-toggle-switch.png');
        const headerToggleSwitchMaterial = new THREE.MeshBasicMaterial({ 
            map: uiHeaderSwitchTexture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });
        headerToggleSwitchMesh = new THREE.Mesh(headerToggleSwitchGeometry, headerToggleSwitchMaterial);
        // Position offset from parent and shifted in Z to avoid z-fighting
        togglePosA = new THREE.Vector3(-0.1, 0, 0.001); // Left position
        togglePosB = new THREE.Vector3(0.05, 0, 0.001);  // Right position
        // Initialize to left (Material)
        headerToggleIsRight = false;
        headerToggleSwitchMesh.position.copy(togglePosA);
        // Make it a child of the header toggle plane
        headerToggleMesh.add(headerToggleSwitchMesh);

        // Load font and create text objects for toggle positions
        const fontLoader = new FontLoader();
        fontLoader.load('/static/fonts/helvetiker_regular.typeface.json', function (font) {
            // Create materials for text (white = default, yellow = active)
            const textDefaultMaterial = new THREE.MeshBasicMaterial({ 
                color: 0xffffff,
                transparent: true,
                opacity: 0.9
            });
            
            const textActiveMaterial = new THREE.MeshBasicMaterial({ 
                color: 0xFFD700, // Same yellow as switch sphere
                transparent: true,
                opacity: 0.9
            });
            
            // Create "Material" text at togglePosA (left position)
            const materialTextGeometry = new TextGeometry('Material', {
                font: font,
                size: 0.02,
                height: 0, // No depth needed for UI text
                curveSegments: 12,
                bevelEnabled: false
            });
            
            materialTextMesh = new THREE.Mesh(materialTextGeometry, textDefaultMaterial);
            // Position at togglePosA with slight Z offset to avoid z-fighting
            materialTextMesh.position.set(togglePosA.x, togglePosA.y - 0.01, togglePosA.z + 0.002);
            // Center the text
            materialTextGeometry.computeBoundingBox();
            materialTextMesh.position.x -= materialTextGeometry.boundingBox.max.x * 0.5;
            headerToggleMesh.add(materialTextMesh);
            
            // Create "Sizes" text at togglePosB (right position)
            const sizesTextGeometry = new TextGeometry('Sizes', {
                font: font,
                size: 0.02,
                height: 0, // No depth needed for UI text
                curveSegments: 12,
                bevelEnabled: false
            });
            
            sizesTextMesh = new THREE.Mesh(sizesTextGeometry, textDefaultMaterial);
            // Position at togglePosB with slight Z offset to avoid z-fighting
            sizesTextMesh.position.set(togglePosB.x, togglePosB.y - 0.01, togglePosB.z + 0.002);
            // Center the text
            sizesTextGeometry.computeBoundingBox();
            sizesTextMesh.position.x -= sizesTextGeometry.boundingBox.max.x * 0.5;
            headerToggleMesh.add(sizesTextMesh);
            
            // Store materials for color switching
            materialTextMesh.userData = {
                defaultMaterial: textDefaultMaterial,
                activeMaterial: textActiveMaterial
            };
            sizesTextMesh.userData = {
                defaultMaterial: textDefaultMaterial,
                activeMaterial: textActiveMaterial
            };
            // Initialize text color for default state
            updateToggleTextColors();
        });

        // Color Panel        
        const colorPanelGeometry = new THREE.PlaneGeometry(0.091, 0.278); 
        // Make colorPanel invisible but keep it as a parent for transitions
        const colorPanelMaterial = new THREE.MeshBasicMaterial({ 
            transparent: true,
            opacity: 0.0,  // Fully transparent
            side: THREE.DoubleSide,
            depthTest: false, 
            depthWrite: false
        });
        const colorPanelMesh = new THREE.Mesh(colorPanelGeometry, colorPanelMaterial);
        colorPanel = colorPanelMesh; // Assign to top-level variable
        colorPanelMesh.position.set(-0.26, 0, 0.06);  
        baseComposition.add(colorPanelMesh);
        
        // Button 1
        const button1Geometry = new THREE.PlaneGeometry(0.06, 0.06); 
        const button1Texture = textureLoader.load('/static/ui_button1.png');   
        const button1Material = new THREE.MeshBasicMaterial({ 
            map: button1Texture,
            transparent: true,
            opacity: 1.0,
            side: THREE.FrontSide, // Optimized: removed DoubleSide
            depthTest: false, 
            depthWrite: false
        });
        const button1Mesh = new THREE.Mesh(button1Geometry, button1Material);
        button1Mesh.position.set(0.0, 0.09, 0); 
        colorPanelMesh.add(button1Mesh);    
        // Store reference to global variable
        window.button1Mesh = button1Mesh;

        // Button 2
        const button2Geometry = new THREE.PlaneGeometry(0.06, 0.06);   
        const button2Texture = textureLoader.load('/static/ui_button2.png');
        const button2Material = new THREE.MeshBasicMaterial({ 
            map: button2Texture,
            transparent: true,
            opacity: 1.0,
            side: THREE.FrontSide, // Optimized: removed DoubleSide
            depthTest: false, 
            depthWrite: false
        });
        const button2Mesh = new THREE.Mesh(button2Geometry, button2Material);     
        button2Mesh.position.set(0, 0, 0); 
        colorPanelMesh.add(button2Mesh);      
        // Store reference to global variable
        window.button2Mesh = button2Mesh;

        // Button 3
        const button3Geometry = new THREE.PlaneGeometry(0.06, 0.06); // Same size as button4Geometry
        const button3Texture = textureLoader.load('/static/ui_button3.png');
        const button3Material = new THREE.MeshBasicMaterial({ 
            map: button3Texture,
            transparent: true,
            opacity: 1.0,
            side: THREE.FrontSide, // Optimized: removed DoubleSide
            depthTest: false, 
            depthWrite: false
        });
        const button3Mesh = new THREE.Mesh(button3Geometry, button3Material);
        button3Mesh.position.set(0, -0.09, 0); 
        colorPanelMesh.add(button3Mesh);
        // Store reference to global variable
        window.button3Mesh = button3Mesh;

        // Button Stroke 1
        const buttonStroke1Geo = new THREE.PlaneGeometry(0.08, 0.08);
        const buttonStrokeTexture = textureLoader.load('/static/ui_buttonstroke.png');
        const buttonStrokeMaterial = new THREE.MeshBasicMaterial({
            map: buttonStrokeTexture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });
        const buttonStroke1Local = new THREE.Mesh(buttonStroke1Geo, buttonStrokeMaterial);
        buttonStroke1Local.position.set(0.0, 0.09, -0.001); // slight z offset to avoid z-fighting
        buttonStroke1Local.scale.set(1, 1, 1); // Start scaled up since button1 is default
        colorPanelMesh.add(buttonStroke1Local);
        buttonStroke1Mesh = buttonStroke1Local; // Store in global variable
        window.buttonStroke1Mesh = buttonStroke1Local; // Also make it globally accessible
        
        // Buttons Stroke 2
        const buttonStroke2Geo = new THREE.PlaneGeometry(0.08, 0.08);
        const buttonStroke2Local = new THREE.Mesh(buttonStroke2Geo, buttonStrokeMaterial);
        buttonStroke2Local.position.set(0.0, 0.0, -0.001); // slight z offset to avoid z-fighting
        buttonStroke2Local.scale.set(0, 0, 0); // Start scaled down
        colorPanelMesh.add(buttonStroke2Local);
        buttonStroke2Mesh = buttonStroke2Local; // Store in global variable
        window.buttonStroke2Mesh = buttonStroke2Local; // Also make it globally accessible

        // Buttons Stroke 3
        const buttonStroke3Geo = new THREE.PlaneGeometry(0.08, 0.08);
        const buttonStroke3Local = new THREE.Mesh(buttonStroke3Geo, buttonStrokeMaterial);
        buttonStroke3Local.position.set(0.0, -0.09, -0.001); // slight z offset to avoid z-fighting
        buttonStroke3Local.scale.set(0, 0, 0); // Start scaled down
        colorPanelMesh.add(buttonStroke3Local);
        buttonStroke3Mesh = buttonStroke3Local; // Store in global variable
        window.buttonStroke3Mesh = buttonStroke3Local; // Also make it globally accessible

        // UI Info Panel
        const infoPanelGeometry = new THREE.PlaneGeometry(0.25, 0.25);
        const uiInfoTexture = textureLoader.load('/static/ui_info.png');
        const infoPanelMaterial = new THREE.MeshBasicMaterial({ 
            map: uiInfoTexture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });
        const infoPanelMesh = new THREE.Mesh(infoPanelGeometry, infoPanelMaterial);
        infoPanel = infoPanelMesh; // Assign to top-level variable   
        infoPanelMesh.position.set(0.2, -.17, 0.16);
        baseComposition.add(infoPanelMesh);
        
        // Switch button
        const sphereGeometry = new THREE.SphereGeometry(0.018, 32, 32);
        const sphereMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xFFD700,
            transparent: false
        });
        switchButton = new THREE.Mesh(sphereGeometry, sphereMaterial);
        switchButton.position.set(0.16, 0.26, 0.0);
        baseComposition.add(switchButton);
        
        // Small plane at switchButton position with ui_pull texture
        const planeGeometry = new THREE.PlaneGeometry(0.066, 0.0462);
        const uiPullTexture = textureLoader.load('/static/ui_pull.png');
        const planeMaterial = new THREE.MeshBasicMaterial({ 
            map: uiPullTexture,
            transparent: true,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        plane.position.set(0.16, 0.31, 0.01);
        pullPlane = plane; // Store reference for animation
        baseComposition.add(plane);
        
        switchRoot = new THREE.Object3D();
        switchRoot.position.copy(switchButton.position);
        baseComposition.add(switchRoot);
        
        // Store the original position of the switch button
        switchButtonOriginalPosition.copy(switchButton.position);

        // Swich button white center
        const sphereDotGeometry = new THREE.SphereGeometry(0.006, 32, 32);
        const sphereDotMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xFFFFFF,
            transparent: false,
            depthTest: false,
            depthWrite: false,
        });
        switchDot = new THREE.Mesh(sphereDotGeometry, sphereDotMaterial);
        
        switchDot.position.set(0.16, 0.26, 0.0);
        baseComposition.add(switchDot);
        
        // String Mesh
        const stringGeo = new THREE.CylinderGeometry(0.003, 0.003, 0.1, 8);
        const stringMat = new THREE.MeshBasicMaterial({ 
            color: 0xffffff, 
            transparent: false, 
            // depthTest: false,
            // depthWrite: false,
        })
        stringMesh = new THREE.Mesh(stringGeo, stringMat);
        // stringMesh.renderOrder = 20;
        baseComposition.add(stringMesh);
        

    },
    function (xhr) {
        // Loading progress
    },
    function (error) {
        // Handle loading error
    }
);

// Function to improve materials with PBR properties for better realism
function improveMaterials(model) {
    if (!model) return;
    
    let materialsImproved = 0;
    
    // Traverse through all children of the model
    model.traverse((child) => {
        if (child.isMesh && child.material) {
            if (Array.isArray(child.material)) {
                // Handle material array
                    child.material.forEach((mat, index) => {
                        const improvedMat = createImprovedMaterial(mat);
                        child.material[index] = improvedMat;
                        materialsImproved++;
                    });
                } else if (child.material) {
                    // Handle single material
                    const improvedMaterial = createImprovedMaterial(child.material);
                    child.material = improvedMaterial;
                    materialsImproved++;
            }
        }
    });
    
}

// Function to create improved PBR materials
function createImprovedMaterial(originalMat) {
    // Determine material type based on name or create varied materials
    let materialProperties = getMaterialProperties(originalMat.name);
    
    // Create a new MeshStandardMaterial with only necessary properties
    const matConfig = {
        color: materialProperties.color,
        roughness: materialProperties.roughness,
        metalness: materialProperties.metalness,
        envMapIntensity: materialProperties.envMapIntensity
    };
    
    // Only add maps that exist in the original material
    if (originalMat.map) matConfig.map = originalMat.map;
    if (originalMat.normalMap) {
        matConfig.normalMap = originalMat.normalMap;
        matConfig.normalScale = new THREE.Vector2(1, 1);
    }
    if (originalMat.roughnessMap) matConfig.roughnessMap = originalMat.roughnessMap;
    if (originalMat.metalnessMap) matConfig.metalnessMap = originalMat.metalnessMap;
    if (originalMat.aoMap) matConfig.aoMap = originalMat.aoMap;
    
    const improvedMat = new THREE.MeshStandardMaterial(matConfig);
    
    // Set material name for debugging
    improvedMat.name = originalMat.name || 'ImprovedMaterial';
    
    // Store reference to Plastic materials for dynamic color changes
    if (improvedMat.name.includes('Plastic')) {
        plasticMaterials.push(improvedMat);
    }
    
    return improvedMat;
}

// Function to check if a button is on cooldown
function isButtonOnCooldown(buttonName) {
    const now = Date.now();
    if (now - buttonCooldowns[buttonName] < BUTTON_COOLDOWN_TIME) {
        return true; // Button is on cooldown
    }
    return false; // Button can be pressed
}

// Function to set button cooldown
function setButtonCooldown(buttonName) {
    buttonCooldowns[buttonName] = Date.now();
}

// Function to change Plastic material colors dynamically
function changePlasticMaterialColor(newColor) {
    if (plasticMaterials.length === 0) return;
    
    // Convert color to THREE.Color if it's not already
    let threeColor;
    if (typeof newColor === 'string') {
        threeColor = new THREE.Color(newColor);
    } else if (typeof newColor === 'number') {
        threeColor = new THREE.Color(newColor);
    } else if (newColor instanceof THREE.Color) {
        threeColor = newColor;
    } else {
        return;
    }
    
    // Change color for all Plastic materials
    plasticMaterials.forEach((material, index) => {
        material.color.copy(threeColor); // Color changes don't require needsUpdate on standard materials
    });
    
}

// Make function globally available for testing
window.changePlasticMaterialColor = changePlasticMaterialColor;

// Function to update sizes material properties
function updateSizesMaterialProperties(properties) {
    if (!sizesMaterial) return;
    
    if (properties.baseColor !== undefined) {
        sizesMaterial.uniforms.baseColor.value.setHex(properties.baseColor);
    }
    if (properties.opacity !== undefined) {
        sizesMaterial.uniforms.opacity.value = properties.opacity;
    }
    
    sizesMaterial.needsUpdate = true;
}

// Make function globally available for testing
window.updateSizesMaterialProperties = updateSizesMaterialProperties;

// Example usage for testing the sizes shader:
// updateSizesMaterialProperties({
//     baseColor: 0x00ff00,      // Green color
//     opacity: 0.9              // Slightly transparent
// });

// Function to update text colors based on toggle state
function updateToggleTextColors() {
    if (!materialTextMesh || !sizesTextMesh) return;
    
    if (headerToggleIsRight) {
        // Button is ON - "Sizes" is active (yellow), "Material" is default (white)
        materialTextMesh.material = materialTextMesh.userData.defaultMaterial;
        sizesTextMesh.material = sizesTextMesh.userData.activeMaterial;
    } else {
        // Button is OFF - "Material" is active (yellow), "Sizes" is default (white)
        materialTextMesh.material = materialTextMesh.userData.activeMaterial;
        sizesTextMesh.material = sizesTextMesh.userData.defaultMaterial;
    }
}

// Set header toggle state (false = Material/left, true = Sizes/right)
function setHeaderToggleState(stateBool, animate = true) {
    headerToggleIsRight = !!stateBool;
    if (headerToggleSwitchMesh && headerToggle && togglePosA && togglePosB) {
        const target = headerToggleIsRight ? togglePosB : togglePosA;
        // Slide the switch with GSAP or snap instantly based on flag
        gsap.killTweensOf(headerToggleSwitchMesh.position);
        if (animate) {
            gsap.to(headerToggleSwitchMesh.position, {
                duration: 0.25,
                x: target.x,
                y: target.y,
                z: target.z,
                ease: "power2.inOut"
            });
        } else {
            headerToggleSwitchMesh.position.copy(target);
        }
    }
    // Update text materials
    updateToggleTextColors();

	// Apply/restore materials based on mode when chair is open
	if (buttonState) {
		if (headerToggleIsRight) {
			applyFresnelToChair(); // SIZES
		} else {
			restoreOriginalMaterials(); // MATERIAL
		}
	}
	
	// Control sizes model visibility based on toggle state
	if (sizesModel) {
		sizesModel.visible = headerToggleIsRight;
	}
}

// Function to determine material properties based on material name
function getMaterialProperties(materialName) {
    const name = materialName || '';
    
    // Metal materials (hardware, screws, mechanisms)
    if (name === 'Metal') {
        return {
            color: 0x888888,        // Medium gray (more realistic)
            roughness: 0.15,         // Slightly rough (more realistic)
            metalness: 0.9,          // Very metallic but not 100%
            envMapIntensity: 1.2     // Moderate reflection (more realistic)
        };
    }
    
    // Black Metal materials (dark metallic parts)
    else if (name === 'BlackMetal') {
        return {
            color: 0x444444,        // Dark gray (more realistic)
            roughness: 0.2,          // Slightly rough (more realistic)
            metalness: 0.9,          // Very metallic but not 100%
            envMapIntensity: 1.0     // Moderate reflection (more realistic)
        };
    }
    
    // Plastic materials (armrests, frame parts)
    else if (name.includes('Plastic')) {
        return {
            color: 0x4682B4,        // Blue color
            // color: 0xF08805,        // Orange color
            // color: 0xB20000,        // Red color
            roughness: 0.25,          // Medium rough
            metalness: 0.0,          // Non-metallic
            envMapIntensity: 2.0     // Medium reflection
        };
    }
    
    // Wood materials (wooden parts)
    else if (name.includes('Wood')) {
        return {
            //color: 0x8b6b47,        // Light brown wood color
            color: 0xBD884A,
            roughness: 0.8,          // Rough (wood-like)
            metalness: 0.0,          // Non-metallic
            envMapIntensity: 0.4     // Low reflection (wood absorbs light)
        };
    }
    
    // Default material (fallback)
    else {
        return {
            color: 0x4682B4,        // Steel blue color
            roughness: 0.7,          // Medium rough
            metalness: 0.1,          // Slightly metallic
            envMapIntensity: 1.0     // Standard reflection
        };
    }
}

/**
 * Lights
 */
const ambientLight = new THREE.AmbientLight(0xffffff, 0.8)
scene.add(ambientLight)

const light = new THREE.DirectionalLight(0xffffff, 1.2);
light.position.set(5, 8, 3);
light.castShadow = true;
light.shadow.mapSize.set(512, 512); // Further reduced for XR performance
light.shadow.bias = -0.0005; // Compensate for reduced resolution
light.shadow.camera.near = 0.5;
light.shadow.camera.far = 50;
light.shadow.camera.left = -10;
light.shadow.camera.right = 10;
light.shadow.camera.top = 10;
light.shadow.camera.bottom = -10;
light.shadow.autoUpdate = false; // Stop updating shadows every frame
scene.add(light);

// Add a second fill light for softer shadows
const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
fillLight.position.set(-3, 6, -2);
scene.add(fillLight);

/**
 * Joint References
 */
const sphere = new THREE.SphereGeometry(0.002, 32, 32)
const defaultMat = new THREE.MeshBasicMaterial({ 
    color: 0xff0000, 
    opacity: 0,
    transparent: true 
})

const lThumbObj = new THREE.Mesh(sphere, defaultMat)
const lIndexObj = new THREE.Mesh(sphere, defaultMat)
const rThumbObj = new THREE.Mesh(sphere, defaultMat)
const rIndexObj = new THREE.Mesh(sphere, defaultMat)

scene.add(lThumbObj)
scene.add(lIndexObj)
scene.add(rThumbObj)
scene.add(rIndexObj)

/**
 * Pinch References
 */
const sphere2 = new THREE.SphereGeometry(0.006, 32, 32)
const defaultMat2 = new THREE.MeshLambertMaterial({ 
    color: 0xffffff,
})
const greenMat2 = new THREE.MeshLambertMaterial({ 
    color: 0x00ff00,
})

const lPinchSphere = new THREE.Mesh(sphere2, defaultMat2)
const rPinchSphere = new THREE.Mesh(sphere2, greenMat2)
scene.add(lPinchSphere)
scene.add(rPinchSphere)
lPinchSphere.visible = false;
rPinchSphere.visible = false; 

/**
 * Hands
 */
var hand1, hand2;

// Get hand tracking without visual meshes for better performance
hand1 = renderer.xr.getHand( 0 );
scene.add( hand1 );

hand2 = renderer.xr.getHand( 1 );
scene.add( hand2 );

const handsGroup = new THREE.Group();
handsGroup.add(hand1, hand2);
scene.add(handsGroup);
handsGroup.visible = false;

const clock = new THREE.Clock();

renderer.setAnimationLoop( function (t, xrFrame) {
    delta = clock.getDelta();
    const ms = delta * 1000;
    
    // Dynamic resolution scaling
    accum = 0.9 * accum + 0.1 * ms; // low-pass filter
    if (accum > targetMs * 1.15 && adaptivePR > 1.0) {
        adaptivePR = Math.max(1.0, adaptivePR - 0.05);
        renderer.setPixelRatio(adaptivePR);
    } else if (accum < targetMs * 0.9 && adaptivePR < MAX_PR) {
        adaptivePR = Math.min(MAX_PR, adaptivePR + 0.05);
        renderer.setPixelRatio(adaptivePR);
    }

    gsap.ticker.tick(delta);
    renderer.render( scene, camera );
        
    // Continuous rotation of the chair
    // if (chairModel) {
    //     chairModel.rotation.y += 0.01; // Adjust speed by changing this value
    // }
        
    handTracking();
    stringUpdate();
    
    // Animate pull plane up and down (only if visible)
    if (pullPlane && pullPlane.visible) {
        const amplitude = 0.005; // How much to move up/down
        const frequency = 1.0; // Speed of oscillation (cycles per second)
        const timeInSeconds = t / 1000; // Convert milliseconds to seconds
        const offset = Math.sin(timeInSeconds * frequency * Math.PI * 2) * amplitude;
        pullPlane.position.y = 0.31 + offset;
    }
    
    positionCompositionAtHeadHeight(); // Call this function in the render loop
} );

function handTracking() {
    
    // Dynamically get available hands - this works with 1 or 2 hands
    const availableHands = [];
    if (hand1 && hand1.joints) availableHands.push(hand1);
    if (hand2 && hand2.joints) availableHands.push(hand2);
    
    // For single hand scenarios, we'll use the available hand for both left and right
    const leftHand = availableHands[0] || null;  // First available hand
    const rightHand = availableHands[1] || availableHands[0] || null;  // Second hand, or same as first if only one hand
    
    // Left Hand Joint References (using hand1 consistently)
    if (leftHand && leftHand.joints && leftHand.joints['thumb-tip']) {
        lThumbObj.position.x = leftHand.joints['thumb-tip'].position.x;
        lThumbObj.position.y = leftHand.joints['thumb-tip'].position.y;
        lThumbObj.position.z = leftHand.joints['thumb-tip'].position.z;
        lThumbObj.rotation.x = leftHand.joints['thumb-tip'].rotation.x;
        lThumbObj.rotation.y = leftHand.joints['thumb-tip'].rotation.y;
        lThumbObj.rotation.z = leftHand.joints['thumb-tip'].rotation.z;
    }

    if (leftHand && leftHand.joints && leftHand.joints['index-finger-tip']) {
        lIndexObj.position.x = leftHand.joints['index-finger-tip'].position.x;
        lIndexObj.position.y = leftHand.joints['index-finger-tip'].position.y;
        lIndexObj.position.z = leftHand.joints['index-finger-tip'].position.z;
        lIndexObj.rotation.x = leftHand.joints['index-finger-tip'].rotation.x;
        lIndexObj.rotation.y = leftHand.joints['index-finger-tip'].rotation.y;
        lIndexObj.rotation.z = leftHand.joints['index-finger-tip'].rotation.z;
    }

    // Right Hand Joint References (using hand2 consistently)
    if (rightHand && rightHand.joints && rightHand.joints['thumb-tip']) {
        rThumbObj.position.x = rightHand.joints['thumb-tip'].position.x;
        rThumbObj.position.y = rightHand.joints['thumb-tip'].position.y;
        rThumbObj.position.z = rightHand.joints['thumb-tip'].position.z;
        rThumbObj.rotation.x = rightHand.joints['thumb-tip'].rotation.x;
        rThumbObj.rotation.y = rightHand.joints['thumb-tip'].rotation.y;
        rThumbObj.rotation.z = rightHand.joints['thumb-tip'].rotation.z;
    }

    if (rightHand && rightHand.joints && rightHand.joints['index-finger-tip']) {
        rIndexObj.position.x = rightHand.joints['index-finger-tip'].position.x;
        rIndexObj.position.y = rightHand.joints['index-finger-tip'].position.y;
        rIndexObj.position.z = rightHand.joints['index-finger-tip'].position.z;
        rIndexObj.rotation.x = rightHand.joints['index-finger-tip'].rotation.x;
        rIndexObj.rotation.y = rightHand.joints['index-finger-tip'].rotation.y;
        rIndexObj.rotation.z = rightHand.joints['index-finger-tip'].rotation.z;
    }

    // Unified Pinch Detection - works with 1 or 2 hands
    // Reset pinch states
    lPinchOn = false;
    rPinchOn = false;
    
    // Check first available hand (left hand logic)
    if (leftHand && leftHand.joints && leftHand.joints['thumb-tip'] && leftHand.joints['index-finger-tip']) {
        const lIndexThumbDist = leftHand.joints['index-finger-tip'].position.distanceTo(leftHand.joints['thumb-tip'].position);
        
        if (lIndexThumbDist < 0.02) {
            lPinchSphere.position.copy(leftHand.joints['thumb-tip'].position);
            lPinchOn = true;
            
            // Chair rotation interaction - start rotation when left hand pinches
            if (!leftHandRotationActive) {
                leftHandRotationActive = true;
                lastLeftHandX = leftHand.joints['thumb-tip'].position.x;
            }
            
            // Calculate horizontal movement for chair rotation
            if (leftHandRotationActive) {
                const currentLeftHandX = leftHand.joints['thumb-tip'].position.x;
                const deltaX = currentLeftHandX - lastLeftHandX;
                       
                // Rotate chair based on horizontal movement
                if (chairModel && Math.abs(deltaX) > 0.001) {
                    chairModel.rotation.y += deltaX * chairRotationSpeed;
                }
                
                lastLeftHandX = currentLeftHandX;
            }
        } else {
            // Stop chair rotation when left hand stops pinching
            if (leftHandRotationActive) {
                leftHandRotationActive = false;
            }
        }
    }
    
    // Check second available hand (right hand logic) - only if we have 2 hands
    if (availableHands.length > 1 && rightHand && rightHand.joints && rightHand.joints['thumb-tip'] && rightHand.joints['index-finger-tip']) {
        const rIndexThumbDist = rightHand.joints['index-finger-tip'].position.distanceTo(rightHand.joints['thumb-tip'].position);
        
        if (rIndexThumbDist < 0.02) {
            rPinchSphere.position.copy(rightHand.joints['thumb-tip'].position);
            rPinchOn = true;
            
            // Chair rotation interaction - start rotation when right hand pinches
            if (!rightHandRotationActive) {
                rightHandRotationActive = true;
                lastRightHandX = rightHand.joints['thumb-tip'].position.x;
            }
            
            // Calculate horizontal movement for chair rotation
            if (rightHandRotationActive) {
                const currentRightHandX = rightHand.joints['thumb-tip'].position.x;
                const deltaX = currentRightHandX - lastRightHandX;

                // Rotate chair based on horizontal movement
                if (chairModel && Math.abs(deltaX) > 0.001) {
                    chairModel.rotation.y += deltaX * chairRotationSpeed;
                }
                
                lastRightHandX = currentRightHandX;
            }
        } else {
            // Stop chair rotation when right hand stops pinching
            if (rightHandRotationActive) {
                rightHandRotationActive = false;
            }
        }
    }
    
    // Simple index-finger touch detection for header toggle (no pinch required)
    // Works with 1 or 2 hands
    if (headerToggle && headerToggleSwitchMesh && togglePosA && togglePosB) {
        headerToggle.localToWorld(_vA.copy(togglePosA));
        headerToggle.localToWorld(_vB.copy(togglePosB));

        const touchThreshold = 0.04; // meters
        let nearLeftSlot = false;
        let nearRightSlot = false;

        // Check all available hands for index finger proximity
        for (let i = 0; i < availableHands.length; i++) {
            const hand = availableHands[i];
            if (hand && hand.joints && hand.joints['index-finger-tip']) {
                const indexWorldPos = hand.joints['index-finger-tip'].position;
                nearLeftSlot = nearLeftSlot || indexWorldPos.distanceTo(_vA) < touchThreshold;
                nearRightSlot = nearRightSlot || indexWorldPos.distanceTo(_vB) < touchThreshold;
            }
        }

        const nowMs = performance.now();
        const cooldownPassed = (nowMs - headerToggleLastTouchMs) > HEADER_TOGGLE_TOUCH_COOLDOWN_MS;
        if ((nearLeftSlot || nearRightSlot) && cooldownPassed) {
            setHeaderToggleState(!headerToggleIsRight);
            headerToggleLastTouchMs = nowMs;
        }
    }

    // Switch Button Interaction - works with 1 or 2 hands
    if (switchButton) {
        let activePinchSphere = null;
        let activePinchOn = false;
        
        // Determine which hand is pinching - prioritize any available hand
        if (lPinchOn && rPinchOn) {
            // Both hands pinching - use the one closer to the button
            const lDistance = lPinchSphere.position.distanceTo(switchButton.position);
            const rDistance = rPinchSphere.position.distanceTo(switchButton.position);
            activePinchSphere = lDistance < rDistance ? lPinchSphere : rPinchSphere;
            activePinchOn = true;
        } else if (lPinchOn) {
            // Left hand pinching (this works for single hand scenarios too)
            activePinchSphere = lPinchSphere;
            activePinchOn = true;
        } else if (rPinchOn) {
            // Right hand pinching (this works for single hand scenarios too)
            activePinchSphere = rPinchSphere;
            activePinchOn = true;
        }
        
        // Handle button interaction if any hand is pinching
        if (activePinchSphere && activePinchOn) {
            // Convert both positions to world coordinates for accurate distance calculation
            const pinchWorldPosition = new THREE.Vector3();
            const buttonWorldPosition = new THREE.Vector3();
            
            activePinchSphere.getWorldPosition(pinchWorldPosition);
            switchButton.getWorldPosition(buttonWorldPosition);
            
            const distanceToButton = pinchWorldPosition.distanceTo(buttonWorldPosition);
            
            // If close enough AND pinching, grab the button
            if (distanceToButton < 0.08 && !switchButtonAttached) {
                switchButtonAttached = true;
            }
            
            // If button is attached, align it with active pinch reference
            if (switchButtonAttached) {
                // Convert pinch world position to button's local coordinate system
                const pinchWorldPosition = new THREE.Vector3();
                activePinchSphere.getWorldPosition(pinchWorldPosition);
                
                // Convert world position to local position relative to button's parent (baseComposition)
                const localPosition = baseComposition.worldToLocal(pinchWorldPosition.clone());
                switchButton.position.copy(localPosition);
            }
        }
        
        // If no hands are pinching and button was attached, animate back to switchRoot
        // This works for both single-hand and dual-hand scenarios
        if (!lPinchOn && !rPinchOn && switchButtonAttached) {
            // Get the switchRoot world position
            const switchRootWorldPosition = new THREE.Vector3();
            switchRoot.getWorldPosition(switchRootWorldPosition);
            
            // Convert world position to local position relative to button's parent (baseComposition)
            const localTargetPosition = baseComposition.worldToLocal(switchRootWorldPosition.clone());
            
            // Toggle the button state immediately when released
            buttonState = !buttonState;
            
            // Trigger chair transition immediately
            updateChairState();
            
            // Animate the button back to the switchRoot position with elastic transition
            gsap.to(switchButton.position, {
                duration: 0.6,
                delay: 0.0,
                x: localTargetPosition.x,
                y: localTargetPosition.y,
                z: localTargetPosition.z,
                ease: "elastic.out(0.5, 0.2)",
                onUpdate: () => {
                    // Ensure the button position is properly updated each frame
                    switchButton.position.needsUpdate = true;
                }
            });
            
            switchButtonAttached = false;
            
            // Count switch pulls and hide hint UI after 2 pulls
            switchPullCount++;
            if (switchPullCount >= 2 && pullPlane) {
                pullPlane.visible = false;
            }

        }
    }
    
    // Button poke detection for color changes - works with 1 or 2 hands
    if (window.button1Mesh && window.button2Mesh && window.button3Mesh) {
        // Only allow button presses when switch is OFF (buttonState = false)
        if (!buttonState) {
            let buttonPressed = false;
            
            // Check all available hands for button interactions
            for (let i = 0; i < availableHands.length; i++) {
                const hand = availableHands[i];
                if (hand && hand.joints && hand.joints['index-finger-tip'] && !buttonPressed) {
                    const indexWorldPos = hand.joints['index-finger-tip'].position;
                    
                    // Button 1 (Blue) - button1Mesh
                    const button1WorldPos = new THREE.Vector3();
                    window.button1Mesh.getWorldPosition(button1WorldPos);
                    const distanceToButton1 = indexWorldPos.distanceTo(button1WorldPos);
                    
                    // Button 2 (Red) - button2Mesh  
                    const button2WorldPos = new THREE.Vector3();
                    window.button2Mesh.getWorldPosition(button2WorldPos);
                    const distanceToButton2 = indexWorldPos.distanceTo(button2WorldPos);
                    
                    // Button 3 (Orange) - button3Mesh
                    const button3WorldPos = new THREE.Vector3();
                    window.button3Mesh.getWorldPosition(button3WorldPos);
                    const distanceToButton3 = indexWorldPos.distanceTo(button3WorldPos);
                    
                    // Check if index finger is close enough to any button (poke detection)
                    if (distanceToButton1 < 0.03 && !isButtonOnCooldown('button1')) {
                        // Button 1 pressed - change to Blue
                        changePlasticMaterialColor(0x4682B4);
                        // Visual feedback - scale button slightly
                        gsap.killTweensOf(window.button1Mesh.scale);
                        gsap.to(window.button1Mesh.scale, { duration: 0.2, x: 0.8, y: 0.8, z: 0.8, ease: "power2.out" });
                        // Update button stroke
                        updateButtonStrokes('button1');
                        setButtonCooldown('button1');
                        buttonResetTimers.button1 = Date.now() + BUTTON_RESET_DELAY;
                        buttonPressed = true;
                    } else if (distanceToButton2 < 0.03 && !isButtonOnCooldown('button2')) {
                        // Button 2 pressed - change to Red
                        changePlasticMaterialColor(0xB20000);
                        // Visual feedback - scale button slightly
                        gsap.killTweensOf(window.button2Mesh.scale);
                        gsap.to(window.button2Mesh.scale, { duration: 0.2, x: 0.8, y: 0.8, z: 0.8, ease: "power2.out" });
                        // Update button stroke
                        updateButtonStrokes('button2');
                        setButtonCooldown('button2');
                        buttonResetTimers.button2 = Date.now() + BUTTON_RESET_DELAY;
                        buttonPressed = true;
                    } else if (distanceToButton3 < 0.03 && !isButtonOnCooldown('button3')) {
                        // Button 3 pressed - change to Orange
                        changePlasticMaterialColor(0xF08805);
                        // Visual feedback - scale button slightly
                        gsap.killTweensOf(window.button3Mesh.scale);
                        gsap.to(window.button3Mesh.scale, { duration: 0.2, x: 0.8, y: 0.8, z: 0.8, ease: "power2.out" });
                        // Update button stroke
                        updateButtonStrokes('button3');
                        setButtonCooldown('button3');
                        buttonResetTimers.button3 = Date.now() + BUTTON_RESET_DELAY;
                        buttonPressed = true;
                    }
                }
            }
        
        // Smart reset button scales to normal size only after delay has passed
        const now = Date.now();
        
        // Only reset Button 1 if reset delay has passed
        if (now > buttonResetTimers.button1) {
            gsap.killTweensOf(window.button1Mesh.scale);
            gsap.to(window.button1Mesh.scale, { duration: 0.2, x: 1.0, y: 1.0, z: 1.0, ease: "power2.out" });
        }
        
        // Only reset Button 2 if reset delay has passed
        if (now > buttonResetTimers.button2) {
            gsap.killTweensOf(window.button2Mesh.scale);
            gsap.to(window.button2Mesh.scale, { duration: 0.2, x: 1.0, y: 1.0, z: 1.0, ease: "power2.out" });
        }
        
        // Only reset Button 3 if reset delay has passed
        if (now > buttonResetTimers.button3) {
            gsap.killTweensOf(window.button3Mesh.scale);
            gsap.to(window.button3Mesh.scale, { duration: 0.2, x: 1.0, y: 1.0, z: 1.0, ease: "power2.out" });
        }
        } // Close the if (!buttonState) condition
    }
}

function rotateToCamera() {
    // Make the base composition face the camera along one horizontal axis (y-axis)
    let targetPosition = camera.position.clone();
    targetPosition.y = baseComposition.position.y; // Keep the y position same to rotate around y-axis only

    let targetQuaternion = new THREE.Quaternion();
    let currentQuaternion = baseComposition.quaternion.clone();

    // Calculate target quaternion for the base composition to look at the target position
    baseComposition.lookAt(targetPosition);
    targetQuaternion.copy(baseComposition.quaternion);
    baseComposition.quaternion.copy(currentQuaternion); // Revert to the original quaternion

    // Smoothly interpolate the rotation
    baseComposition.quaternion.slerp(targetQuaternion, 0.1);
}

function stringUpdate() {
    if (!stringMesh || !switchButton || !switchRoot) return;
    
    // Get local positions and calculate direction
    const A = _vA.copy(switchRoot.position);
    const B = _vB.copy(switchButton.position);
    const dir = _vC.subVectors(B, A);
    const dist = dir.length();
    
    // Position string at midpoint using addScaledVector for efficiency
    stringMesh.position.copy(A).addScaledVector(dir, 0.5);
    
    // Scale the cylinder to match the distance between points
    stringMesh.scale.setY(dist / 0.1); // Only update Y scale since X,Z are constant
    
    // Calculate the rotation needed to align Y-axis with the direction vector
    if (dist > 0.001) { // Avoid division by zero
        dir.normalize();
        _q.setFromUnitVectors(UP_VECTOR, dir); // Using constant UP_VECTOR
        stringMesh.quaternion.copy(_q);
    }
}

function updateChairState() {
    if (!chairModel) return;
    
    // Force shadow update when chair state changes
    renderer.shadowMap.needsUpdate = true;
    light.shadow.needsUpdate = true;
    
    if (buttonState) {
        // Button is ON - transform chair to OPEN state
        
        // Animate scale change
        gsap.to(chairModel.scale, {
            duration: 0.5,
            x: 1.1,
            y: 1.1,
            z: 1.1,
            ease: "power2.out"
        });
        
        // Animate position change
        gsap.to(chairModel.position, {
            duration: 0.5,
            x: 0,
            y: -1.35,
            z: 0,
            ease: "power2.out"
        });
        
        // Scale uiFrame and infoPanel to 0 when button is ON
        if (uiFrame) {
            gsap.to(uiFrame.scale, {
                duration: 0.3,
                x: 0.0,
                y: 0.0,
                z: 0.0,
                ease: "power2.out"
            });
        }
        if (infoPanel) {
            gsap.to(infoPanel.scale, {
                duration: 0.3,
                x: 0.0,
                y: 0.0,
                z: 0.0,
                ease: "power2.out"
            });
        }
        
        // Scale colorPanel to 0 when button is ON (same behavior as uiFrame and infoPanel)
        if (colorPanel) {
            gsap.to(colorPanel.scale, {
                duration: 0.3,
                x: 0.0,
                y: 0.0,
                z: 0.0,
                ease: "power2.out"
            });
        }
        
        // Scale the headerToggle to full size when button is ON
        if (headerToggle) {
            gsap.to(headerToggle.scale, {
                duration: 0.3,
                x: 1.0,
                y: 1.0,
                z: 1.0,
                ease: "power2.out"
            });
        }
        
        // Update text colors for toggle state
        updateToggleTextColors();

		// Apply materials based on current toggle mode when opening
		if (headerToggleIsRight) {
			applyFresnelToChair();
		} else {
			restoreOriginalMaterials();
		}
		
		// Show sizes model only if in Sizes view when opening
		if (sizesModel) {
			sizesModel.visible = headerToggleIsRight;
		}
        
    } else {
        // Button is OFF - transform chair back to closed state
        
        // Animate scale back to original
        gsap.to(chairModel.scale, {
            duration: 0.4,
            x: 0.5,
            y: 0.5,
            z: 0.5,
            ease: "power2.out"
        });
        
        // Animate position back to original
        gsap.to(chairModel.position, {
            duration: 0.4,
            x: 0,
            y: -0.25,
            z: 0,
            ease: "power2.out"
        });
        
        // Scale uiFrame and infoPanel back to original size when button is OFF
        if (uiFrame) {
            gsap.to(uiFrame.scale, {
                duration: 0.3,
                x: 1.0,
                y: 1.0,
                z: 1.0,
                ease: "power2.out"
            });
        }
        if (infoPanel) {
            gsap.to(infoPanel.scale, {
                duration: 0.3,
                x: 1.0,
                y: 1.0,
                z: 1.0,
                ease: "power2.out"
            });
        }
        
        // Scale colorPanel back to original size when button is OFF (same behavior as uiFrame and infoPanel)
        if (colorPanel) {
            gsap.to(colorPanel.scale, {
                duration: 0.3,
                x: 1.0,
                y: 1.0,
                z: 1.0,
                ease: "power2.out"
            });
        }
        
        // Scale the headerToggle back to 0 when button is OFF
        if (headerToggle) {
            gsap.to(headerToggle.scale, {
                duration: 0.3,
                x: 0.0,
                y: 0.0,
                z: 0.0,
                ease: "power2.out"
            });
            // Reset header toggle to A (Material) so next opening starts at A (no animation)
            setHeaderToggleState(false, false);
        }
        
        // Update text colors for toggle state
        updateToggleTextColors();

		// Always restore original materials when closing
		restoreOriginalMaterials();
		
		// Hide sizes model when closing
		if (sizesModel) {
			sizesModel.visible = false;
		}
    }
}

// Function to update button stroke scaling based on which button is pressed
function updateButtonStrokes(activeButton) {
    if (!buttonStroke1Mesh || !buttonStroke2Mesh || !buttonStroke3Mesh) return;
    
    // Scale all strokes down immediately (no delay)
    gsap.to(buttonStroke1Mesh.scale, { duration: 0.2, x: 0, y: 0, z: 0, ease: "power2.out" });
    gsap.to(buttonStroke2Mesh.scale, { duration: 0.2, x: 0, y: 0, z: 0, ease: "power2.out" });
    gsap.to(buttonStroke3Mesh.scale, { duration: 0.2, x: 0, y: 0, z: 0, ease: "power2.out" });
    
    // Scale up the active button's stroke with a 0.3-second delay
    const STROKE_DELAY = 0.3; // 0.3 seconds delay
    
    switch(activeButton) {
        case 'button1':
            gsap.to(buttonStroke1Mesh.scale, { 
                duration: 0.2, 
                delay: STROKE_DELAY,
                x: 1, y: 1, z: 1, 
                ease: "power2.out" 
            });
            break;
        case 'button2':
            gsap.to(buttonStroke2Mesh.scale, { 
                duration: 0.2, 
                delay: STROKE_DELAY,
                x: 1, y: 1, z: 1, 
                ease: "power2.out" 
            });
            break;
        case 'button3':
            gsap.to(buttonStroke3Mesh.scale, { 
                duration: 0.2, 
                delay: STROKE_DELAY,
                x: 1, y: 1, z: 1, 
                ease: "power2.out" 
            });
            break;
    }
}